/**
 * @fileoverview Banner component for full-width announcements
 * @module ctl-banner
 * @version 1.0.0
 * @deprecated Use ctl-message with variant="banner" instead. This component is maintained for backwards compatibility.
 * 
 * @description
 * A banner component for displaying full-width announcements and notifications.
 * 
 * **DEPRECATED**: This component has been superseded by the unified `ctl-message` component.
 * Please use `<ctl-message variant="banner">` instead. This legacy component is retained for backwards compatibility.
 * 
 * @example
 * <!-- DEPRECATED: Old way -->
 * <ctl-banner tone="warning" open>Trial expires in 5 days.</ctl-banner>
 * 
 * <!-- RECOMMENDED: New way -->
 * <ctl-message variant="banner" tone="warning" open>Trial expires in 5 days.</ctl-message>
 */

import './ctl-message.js';
import { dispatchEvent } from '../../../utils/events.js';
import { shadowQuery } from '../../../utils/dom.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    display: none;
    position: relative;
    width: 100%;
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    border-bottom: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    font: var(--ctl-font-weight-normal) var(--ctl-font-size-base) var(--ctl-font-sans, system-ui);
    line-height: var(--ctl-leading-normal);
    z-index: var(--ctl-z-dropdown);
  }

  :host([open]) {
    display: block;
  }

  :host([sticky]) {
    position: sticky;
    top: 0;
  }

  /* Tone colors - neutral (default) */
  :host(:not([tone]), [tone="neutral"]) {
    background: var(--ctl-color-surface);
    border-bottom-color: var(--ctl-color-border);
  }

  /* Tone colors - info */
  :host([tone="info"]) {
    background: color-mix(in srgb, var(--ctl-color-info) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-info) 30%, var(--ctl-color-border));
  }

  /* Tone colors - success */
  :host([tone="success"]) {
    background: color-mix(in srgb, var(--ctl-color-success) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-success) 30%, var(--ctl-color-border));
  }

  /* Tone colors - warning */
  :host([tone="warning"]) {
    background: color-mix(in srgb, var(--ctl-color-warning) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-warning) 30%, var(--ctl-color-border));
  }

  /* Tone colors - danger */
  :host([tone="danger"]) {
    background: color-mix(in srgb, var(--ctl-color-danger) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-danger) 30%, var(--ctl-color-border));
  }

  .banner-container {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-3);
    padding: var(--ctl-space-3) var(--ctl-space-4);
    max-width: 1400px;
    margin: 0 auto;
  }

  .banner-content {
    flex: 1;
    min-width: 0;
  }

  .banner-actions {
    flex-shrink: 0;
    display: flex;
    gap: var(--ctl-space-2);
    align-items: center;
  }

  .banner-actions:empty {
    display: none;
  }

  .close-btn {
    flex-shrink: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: var(--ctl-space-1-5);
    line-height: 1;
    opacity: var(--ctl-opacity-muted);
    transition: opacity var(--ctl-transition-normal);
    color: currentColor;
    font-size: var(--ctl-space-5);
  }

  .close-btn:hover {
    opacity: 1;
  }

  :host(:not([closeable])) .close-btn {
    display: none;
  }
</style>

<div class="banner-container" part="container">
  <div class="banner-content" part="content">
    <slot></slot>
  </div>
  <div class="banner-actions" part="actions">
    <slot name="actions"></slot>
  </div>
  <button class="close-btn" aria-label="Close" part="close-button">×</button>
</div>
`;

export class CtlBanner extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  static get observedAttributes() {
    return ["open", "tone", "sticky", "closeable"];
  }

  connectedCallback() {
    this._closeHandler = () => this.close();
    const closeBtn = shadowQuery(this, ".close-btn");
    closeBtn.addEventListener("click", this._closeHandler);

    // Set defaults
    if (!this.hasAttribute("tone")) this.setAttribute("tone", "info");

    // Set role for accessibility
    this._updateRole();
  }

  disconnectedCallback() {
    const closeBtn = shadowQuery(this, ".close-btn");
    if (closeBtn && this._closeHandler) {
      closeBtn.removeEventListener("click", this._closeHandler);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open") {
      if (newValue !== null && oldValue === null) {
        this._dispatchEvent("ctl-open");
      } else if (newValue === null && oldValue !== null) {
        this._dispatchEvent("ctl-close");
      }
    } else if (name === "tone" && oldValue !== newValue) {
      this._updateRole();
    }
  }

  _updateRole() {
    const tone = this.getAttribute("tone");
    if (tone === "danger" || tone === "warning") {
      this.setAttribute("role", "alert");
    } else {
      this.setAttribute("role", "status");
    }
  }

  open(reason = "programmatic") {
    if (this.hasAttribute("open")) return;
    this.setAttribute("open", "");
  }

  close(reason = "user") {
    if (!this.hasAttribute("open")) return;
    this.removeAttribute("open");
  }

  _dispatchEvent(name, detail = {}) {
    dispatchEvent(this, name, detail);
  }
}

customElements.define("ctl-banner", CtlBanner);
